import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, Stack, TextField, Typography } from "@mui/material";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import StrongPasswordPopper from "components/StrongPasswordPopper/StrongPasswordPopper";
import { SHA256 } from "crypto-js";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { validateStrongPassword } from "utils/utils";
import { resetPassword } from "./model";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";

const PAGE_LOCALE_KEY = "page.changePassword.";

const ChangePassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { snackbarError } = useSnackbarMAS();

  //Uses null to distinguish untouched from empty
  const [password, setPassword] = useState(null);
  const [passwordVisibility, setPasswordVisibility] = useState(false);

  const [confirmPassword, setConfirmPassword] = useState(null);
  const [confirmPasswordVisibility, setConfirmPasswordVisibility] =
    useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordTipsOpen, setPasswordTipsOpen] = useState(false);

  const [isAwaitingResetResponse, setIsAwaitingResetResponse] = useState(false);

  const isPasswordValid = validateStrongPassword(password);
  const invalidChecking = useMemo(() => {
    return {
      pwEmpty: !password,
      pwEmptyTouched: password !== null && !password,
      pwInvalid: password && !isPasswordValid,
      cPwEmpty: !confirmPassword,
      cPwEmptyTouched: confirmPassword !== null && !confirmPassword,
      pwNotMatch: confirmPassword && password !== confirmPassword,
    };
  }, [password, confirmPassword]);
  const areFieldsInvalid = Object.values(invalidChecking).some(Boolean);

  const passwordHelperText = () => {
    if (invalidChecking.pwEmptyTouched) {
      return t(PAGE_LOCALE_KEY + "fields.password.error.empty");
    }
    return " "; //workaround to keep helper text expanded, see https://mui.com/material-ui/react-text-field/#helper-text
  };

  const confirmPasswordHelperText = () => {
    if (invalidChecking.cPwEmptyTouched) {
      return t(PAGE_LOCALE_KEY + "fields.confirmPassword.error.empty");
    } else if (invalidChecking.pwNotMatch && !invalidChecking.cPwEmpty) {
      return t(PAGE_LOCALE_KEY + "fields.confirmPassword.error.mismatch");
    }
    return " "; //workaround to keep helper text expanded, see https://mui.com/material-ui/react-text-field/#helper-text
  };

  const handleChangePassword = async () => {
    invalidChecking.pwEmpty && setPassword("");
    invalidChecking.cPwEmpty && setConfirmPassword("");
    if (areFieldsInvalid) return;

    const query = new URLSearchParams(location.search);

    const payload = {
      token: query.get("token"),
      newPassword: SHA256(password).toString(),
    };

    if (!payload.token) {
      snackbarError({
        message: t(PAGE_LOCALE_KEY + `message.fail.noToken`),
      });
      return;
    }

    setIsAwaitingResetResponse(true);
    const apiResponse = await resetPassword(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbarError({ message: t("general.message.networkError") });
      setIsAwaitingResetResponse(false);
      return;
    }

    switch (apiResponse.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        snackbarError({ message: t("general.message.unauthorized") });
        break;
      }
      default: {
      }
    }

    const responseBody = apiResponse.data;
    switch (responseBody.responseCode) {
      case "0000": {
        navigate("/user/change-password-result", { replace: true });
        break;
      }
      case "4304":
      case "4313":
      case "4314":
      case "5999":
        snackbarError({
          message: t(
            PAGE_LOCALE_KEY + `message.fail.${responseBody.responseCode}`,
          ),
        });
        break;
      default: {
        snackbarError({
          message:
            t(PAGE_LOCALE_KEY + "message.fail") +
            (responseBody.message ? ` (${responseBody.message})` : ""),
        });
      }
    }

    setIsAwaitingResetResponse(false);
  };

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      gap="8px"
      sx={{ width: "380px", maxWidth: "80%" }}
    >
      <Typography textAlign="center" variant="h2" sx={{ marginY: "24px" }}>
        {t(PAGE_LOCALE_KEY + "label.title")}
      </Typography>
      <TextField
        disabled={isAwaitingResetResponse}
        variant="standard"
        type={passwordVisibility ? "text" : "password"}
        label={t(PAGE_LOCALE_KEY + "fields.password.label")}
        InputLabelProps={{ shrink: true }}
        value={password ?? ""}
        onChange={(e) => setPassword(e.target.value)}
        onFocus={(e) => {
          setAnchorEl(e.currentTarget.parentElement);
          setPasswordTipsOpen(true);
        }}
        onBlur={() => {
          setPasswordTipsOpen(false);
        }}
        helperText={passwordHelperText()}
        error={invalidChecking.pwEmptyTouched || invalidChecking.pwInvalid}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleChangePassword();
          }
        }}
        sx={{
          "& .MuiInput-root": {
            height: "40px",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ButtonMAS
                variant="text-only"
                onClick={() => setPasswordVisibility(!passwordVisibility)}
                style={{
                  minHeight: 32,
                  maxHeight: 32,
                  minWidth: 32,
                  maxWidth: 32,
                  padding: 0,
                  borderRadius: 16,
                }}
              >
                {passwordVisibility ? (
                  <Visibility sx={{ fontSize: "18px", color: "#666" }} />
                ) : (
                  <VisibilityOff sx={{ fontSize: "18px", color: "#666" }} />
                )}
              </ButtonMAS>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        disabled={isAwaitingResetResponse}
        variant="standard"
        type={confirmPasswordVisibility ? "text" : "password"}
        label={t(PAGE_LOCALE_KEY + "fields.confirmPassword.label")}
        InputLabelProps={{ shrink: true }}
        value={confirmPassword ?? ""}
        onChange={(e) => setConfirmPassword(e.target.value)}
        helperText={confirmPasswordHelperText()}
        error={invalidChecking.cPwEmptyTouched || invalidChecking.pwNotMatch}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleChangePassword();
          }
        }}
        sx={{
          "& .MuiInput-root": {
            height: "40px",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ButtonMAS
                variant="text-only"
                onClick={() =>
                  setConfirmPasswordVisibility(!confirmPasswordVisibility)
                }
                style={{
                  minHeight: 32,
                  maxHeight: 32,
                  minWidth: 32,
                  maxWidth: 32,
                  padding: 0,
                  borderRadius: 16,
                }}
              >
                {confirmPasswordVisibility ? (
                  <Visibility sx={{ fontSize: "18px", color: "#666" }} />
                ) : (
                  <VisibilityOff sx={{ fontSize: "18px", color: "#666" }} />
                )}
              </ButtonMAS>
            </InputAdornment>
          ),
        }}
      />
      <Typography variant="content">
        {t(PAGE_LOCALE_KEY + "requirement.unique")}
      </Typography>
      <ButtonMAS
        disabled={areFieldsInvalid || isAwaitingResetResponse}
        variant="auth-button"
        isLoading={isAwaitingResetResponse}
        onClick={handleChangePassword}
        style={{ width: "100%", minHeight: "45px", margin: "16px 0" }}
      >
        {t(PAGE_LOCALE_KEY + "button.actionSubmit")}
      </ButtonMAS>
      <StrongPasswordPopper
        open={passwordTipsOpen && !isAwaitingResetResponse}
        anchorEl={anchorEl}
        password={password}
      />
    </Stack>
  );
};

export default ChangePassword;
